import * as React from 'react';
import * as ReactDom from 'react-dom';
import * as d3 from 'd3';
import './InclinationChart.css';

interface InclinationMidrangeChartProps {
    value: number;
    midrangeLow: number;
    midrangeHigh: number;
    style?: string;
    small?: boolean;
}

interface InclinationMidrangeChartState {
    value: number;
    midrangeLow: number;
    midrangeHigh: number;
    small?: boolean;
    previousSmall?: boolean; // we'll keep track of the previous isSmall state so that we can transition smoother.
}

export class InclinationMidrangeChart extends React.Component<InclinationMidrangeChartProps, InclinationMidrangeChartState> {
    private inclinationRect: d3.Selection<Element | d3.EnterElement | Document | Window | null, {}, null, undefined>;
    private inclinationText: d3.Selection<Element | d3.EnterElement | Document | Window | null, {}, null, undefined>;
    private barOffset = 0;

    // minimum values so the inital load doesn't look weird
    private minimumTextOffset = 12;
    private svgContainer: any = undefined;
    private element: any = undefined;
    private rect1: any = undefined;
    private midrangeBar: any = undefined;
    private midrangeTooltip: any = undefined;

    constructor(props: InclinationMidrangeChartProps) {
        super(props);

        this.state = {
            value: this.props.value,
            midrangeLow: this.props.midrangeLow,
            midrangeHigh: this.props.midrangeHigh,
            small: this.props.small,
            previousSmall: undefined
        };
    }

    componentDidMount() {
        this.buildInclinationChart(this.props.midrangeLow, this.props.midrangeHigh);
        this.updateInclinationChart(this.props.value, this.props.midrangeLow, this.props.midrangeHigh, this.props.small);
        this.redraw.bind(this);
        window.addEventListener('resize', this.redraw.bind(this));
        window.addEventListener('side-menu-toggle', this.redraw.bind(this));
    }

    componentWillReceiveProps(nextProps: InclinationMidrangeChartProps) {
        this.setState({
            value: nextProps.value,
            midrangeLow: nextProps.midrangeLow,
            midrangeHigh: nextProps.midrangeHigh,
            small: nextProps.small,
            previousSmall: this.state.small
        });

        this.updateInclinationChart(nextProps.value, this.props.midrangeLow, this.props.midrangeHigh, nextProps.small);
    }
    
    redraw() {
        setTimeout(
            () => {
                this.svgContainer
                    .attr('width', this.element.clientWidth)
                    .attr('height', this.element.clientHeight);
        
                this.updateInclinationChart(this.props.value, this.props.midrangeLow, this.props.midrangeHigh);
                this.forceUpdate();
            }, 
            100);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.redraw.bind(this));
        window.removeEventListener('side-menu-toggle', this.redraw.bind(this));
    }

    buildInclinationChart(midrangeLow: number, midrangeHigh: number) {
        this.element = ReactDom.findDOMNode(this);

        this.svgContainer = d3.select(this.element).append('svg')
            .attr('width', this.element.clientWidth)
            .attr('height', this.element.clientHeight);

        let adjustedClientWidth = this.element.clientWidth - this.barOffset;
        if (adjustedClientWidth < 0) { adjustedClientWidth = 0; }

        // more pronounced section background
        this.rect1 = this.svgContainer.append('rect')
            .attr('x', 0)
            .attr('y', 7)
            .attr('width', adjustedClientWidth)
            .attr('height', this.element.clientHeight - 14)
            .attr('fill', 'rgba(0, 34, 62, 0.25)');

        // midrange tooltip
        this.midrangeTooltip = d3.select('body')
            .append('div')
            .attr('class', 'chart-tooltip')
            .text(`${midrangeLow} - ${midrangeHigh}`);

        var midrangeTooltip = this.midrangeTooltip;

        // midrange bar
        this.midrangeBar = this.svgContainer.append('rect')
            .attr('x', (adjustedClientWidth * (midrangeLow / 100)))
            .attr('y', 7)
            .attr('width', adjustedClientWidth * ((midrangeHigh - midrangeLow) / 100))
            .attr('height', this.element.clientHeight - 14)
            .attr('fill', '#00223e')
            .on('mouseover', function () { return midrangeTooltip.style('visibility', 'visible'); })
            .on('mousemove', function () {
                return midrangeTooltip.style('top', (d3.event.pageY - 10) + 'px').style('left', (d3.event.pageX + 10) + 'px');
            })
            .on('mouseout', function () { return midrangeTooltip.style('visibility', 'hidden'); });

        let circleY, circleWidth, circleHeight, textX;
        if (this.props.small === true) {
            circleY = 5;
            circleWidth = this.element.clientHeight - 10;
            circleHeight = this.element.clientHeight - 10;
            textX = this.element.clientHeight / 2.0 - 8;
        } else {
            circleY = 0;
            circleWidth = this.element.clientHeight;
            circleHeight = this.element.clientHeight;
            textX = this.element.clientHeight / 2.0;
        }
        
        // inclination value rectangle
        this.inclinationRect = this.svgContainer.append('rect')
            .attr('x', 0)
            .attr('y', circleY)
            .attr('rx', this.element.clientHeight )
            .attr('ry', this.element.clientHeight )
            .attr('width', circleWidth )
            .attr('height', circleHeight )
            .attr('fill', '#b8271c')
            .attr('class', 'pva-style-circle ' + this.props.style);

        // inclination value text
        this.inclinationText = this.svgContainer.append('text')
            .attr('x', textX)
            .attr('y', this.element.clientHeight /  2.0 + 4)
            .attr('font-size', 12)
            .attr('font-weight', 'bold')
            .attr('fill', 'white')
            .text(0);
    }

    updateInclinationChart(inclinationValue: number, midrangeLow: number, midrangeHigh: number, isSmall?: boolean) {
        this.rect1.attr('width', this.element.clientWidth);
        
        let adjustedClientWidth = this.element.clientWidth - this.barOffset;
        if (adjustedClientWidth < 0) { adjustedClientWidth = 0; }

        this.midrangeBar
            .attr('x', (adjustedClientWidth * (midrangeLow / 100)))
            .attr('width', adjustedClientWidth * ((midrangeHigh - midrangeLow) / 100));
        // const element = ReactDom.findDOMNode(this);

        this.midrangeTooltip.text(`${midrangeLow} - ${midrangeHigh}`);

        // we're subtracting 1 from the inclination value because the bar to mark it is width 3
        // so we need to move it one back to the left so the center of it lines up with the inclination value
        let factor = inclinationValue - 1;

        if (factor < 0) { factor = 0; }

        let newX = ((this.element.clientWidth) * (factor / 100)) - 10;
        if (newX > this.element.clientWidth - (this.element.clientHeight / 2.0) - 16) {
            newX = this.element.clientWidth - (this.element.clientHeight / 2.0) - 16;
        }

        if (newX < 0) {
            newX = 0;
        }

        let textOffset;
        if (inclinationValue < 9) {
            textOffset = isSmall !== true ? this.minimumTextOffset : this.minimumTextOffset - 5;
        } else {
            textOffset = isSmall !== true ? 8 : 4;
        }

        let circleY, circleWidth, circleHeight;
        if (isSmall === true) {
            circleY = 5;
            circleWidth = this.element.clientHeight - 10;
            circleHeight = this.element.clientHeight - 10;
        } else {
            circleY = 0;
            circleWidth = this.element.clientHeight;
            circleHeight = this.element.clientHeight;
        }

        this.inclinationRect.transition().duration(0)
            .attr('y', circleY)
            .attr('width', circleWidth)
            .attr('height', circleHeight);

        this.inclinationRect.transition().duration(2000)
            .attr('x', newX);

        this.inclinationText
            .transition().duration(2000)
            .tween('text', (d) => {
                const currentValue = Number(this.inclinationText.text());
                const interpolator = d3.interpolateNumber(currentValue, inclinationValue);

                return (t) => {
                    this.inclinationText.text(`${Math.round(interpolator(t))}`);
                };
            }).
            attr('x', newX + textOffset);
    }

    render() {
        return (
            <div className="inclination-chart" />
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/charts/InclinationMidrangeChart.tsx